import React, { useState, useEffect } from "react";
import CheckmarkIcon from "@react-spectrum/s2/icons/Checkmark";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  View,
  Flex,
  Text,
  Button,
  Link,
  RadioGroup,
  Radio,
  asS2Icon,
} from "./Common/spectrumCompat";
import { displayToasts } from "../Helpers/displayAppToast";
import FullScreenLoader from "./Common/FullScreenLoader";
import DxpRegistrationTopHeader from "./DxpRegistrationTopHeader";
import WizardNavLayout from "./WizardNavLayout";
import { setPartnerRegistrationFormStep } from "../Redux/actions";
import {
  flattenWithSeparator,
  unflattenWithSeparator,
  getStoredMembershipType,
  persistPartnerLevel,
} from "../Helpers/utils";
import { saveRegistrationForm } from "../Services/PartnerRegistrationAPI";
import { PROGRAM_SPECIFIC_NAMES } from "../Helpers/constants";
import useUnsavedChangesWarning from "../Hooks/useUnsavedChangesWarning";
import {
  validateCompanyInfo,
  clearCompanyValidationInlineErrors,
  hasAnyCompanyFieldInput,
  scheduleShowCompanyValidationErrorsBelowFields,
} from "../Helpers/companyValidationUtils";
import { applyDxpPersonalInfoRegisterDefaults } from "../Helpers/dxpRegistrationValidation";
import membershipStepConfig from "../ContentConfig/MembershipStep";
import "./css/registration.css";

const Checkmark = asS2Icon(CheckmarkIcon);

/* eslint-disable @typescript-eslint/no-explicit-any */
const MEMBERSHIP_OPTIONS = [...membershipStepConfig.membershipOptions] as any[];

const PROGRAM = "DXP";

type MembershipOptionsProps = {
  /** Dev preview: no Redux step sync; safe back/submit. */
  previewMode?: boolean;
};

/**
 * Outlined pills — Back + Save and exit.
 * Visual rules live in `dxp-wizard-inline-migrated.css` / `registration.css`.
 */
const secondaryFooterPillLabelClass = "dxp-membership-secondary-footer-pill";

const primaryFooterPillLabelClass = "dxp-membership-primary-footer-pill";

function MembershipOptions({ previewMode = false }: MembershipOptionsProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const registration = useSelector((state: any) => state?.registration || {});
  const accountId = localStorage.getItem("accountId");

  const [membershipType, setMembershipType] = useState<string>("Silver");
  const [expandedOptions, setExpandedOptions] = useState<
    Record<string, boolean>
  >({});
  const [isLoading, setIsLoading] = useState(false);

  const allowNavigation = useUnsavedChangesWarning(
    true,
    "Changes done in form may not be saved. Are you sure you want to leave?",
  );

  useEffect(() => {
    if (previewMode) return;
    if (registration?.partnerRegistrationFormStep !== 5) {
      dispatch(setPartnerRegistrationFormStep(5));
    }
  }, [dispatch, registration?.partnerRegistrationFormStep, previewMode]);

  useEffect(() => {
    const persistedStr = sessionStorage.getItem("registrationFormData");
    if (!persistedStr) return;
    try {
      const p: Record<string, any> = JSON.parse(persistedStr);
      if (p.membershipInfo_membershipType) {
        setMembershipType(p.membershipInfo_membershipType);
      }
    } catch {
      // ignore
    }
  }, []);

  const handleBack = () => {
    if (previewMode) {
      displayToasts({
        type: "info",
        text: "Preview mode — back is disabled.",
        autoClose: true,
      });
      return;
    }
    dispatch(setPartnerRegistrationFormStep(4));
    window.scrollTo(0, 0);
  };

  async function submitForm(action: string) {
    if (previewMode) {
      displayToasts({
        type: "info",
        text: "Preview mode — submit is disabled.",
        autoClose: true,
      });
      return;
    }
    const persistedStr = sessionStorage.getItem("registrationFormData");
    let formData: Record<string, any> = {};
    try {
      if (persistedStr) formData = JSON.parse(persistedStr);
    } catch {
      displayToasts({
        type: "error",
        text: "Please complete the previous steps first.",
        autoClose: true,
      });
      return;
    }

    const draft = registration?.dxpUserProfileDraft as
      | { personalInfo?: Record<string, unknown> }
      | null
      | undefined;
    if (draft?.personalInfo && typeof draft.personalInfo === "object") {
      const flatPersonal = flattenWithSeparator(
        { personalInfo: draft.personalInfo },
        "_",
      );
      formData = { ...flatPersonal, ...formData };
    }

    formData.membershipInfo_membershipType = membershipType;

    const isBenefitsCenterReCheckout =
      action === "proceedToBenefitsCenter" &&
      localStorage.getItem("redirectTo") === "benefitsCenter" &&
      !!accountId;

    if (
      (action === "register" || action === "proceedToBenefitsCenter") &&
      !isBenefitsCenterReCheckout
    ) {
      let companyErrors: Array<{ instancePath: string; message: string }> = [];
      try {
        companyErrors = validateCompanyInfo(formData) || [];
      } catch {
        companyErrors = [
          {
            instancePath: "/companyInfo_companyName",
            message: "Please fix the errors below.",
          },
        ];
      }
      if (companyErrors.length > 0 && hasAnyCompanyFieldInput(formData)) {
        scheduleShowCompanyValidationErrorsBelowFields(companyErrors);
        displayToasts({
          type: "error",
          text: "Please fix errors on Company Information before registering.",
          autoClose: true,
        });
        dispatch(setPartnerRegistrationFormStep(4));
        window.scrollTo(0, 0);
        return;
      }
      clearCompanyValidationInlineErrors();
    }

    const data = JSON.parse(JSON.stringify(formData));
    delete data.step;
    delete data.type;
    delete data.affiliatedCompanyName;
    delete data.previouslyRegistered;

    data.action = action;
    if (action === "proceedToBenefitsCenter") {
      data.action = "register";
      data.companyInfo_redirectTo = "benefitsCenter";
    }
    data.companyInfo_isSubsidiary = data.companyInfo_isSubsidiary === "Yes";
    if (action === "save-exit") {
      delete data.membershipInfo_membershipType;
    }

    delete data.companyInfo_initialPhone;
    delete data.companyInfo_initialPhoneCountryCode;
    delete data.companyInfo_phoneWithoutCountryCode;
    delete data.personalInfo_initialPhone;
    delete data.personalInfo_initialPhoneCountryCode;
    delete data.personalInfo_phoneWithoutCountryCode;

    if (data.personalInfo_jobFunction !== "Other") {
      delete data.personalInfo_customJobFunction;
    }

    const currentSelectedCompanyType =
      registration?.selectedRegisterCompanyType ||
      localStorage.getItem("selectedRegisterCompanyType");
    data.registrationType = ["standaloneCompany", "subsidaryCompany"].includes(
      currentSelectedCompanyType || "",
    )
      ? "Primary"
      : "Secondary";

    const payload: any = unflattenWithSeparator(data, "_");
    applyDxpPersonalInfoRegisterDefaults(payload);

    if (payload.companyInfo?.primaryBusiness) {
      const primaryBusinessMapping: Record<string, string> = {
        "Consulting/Professional Services": "Solution",
        "ISV/Data Provider": "Technology",
      };
      payload.companyInfo.primaryBusiness =
        primaryBusinessMapping[payload.companyInfo.primaryBusiness] ??
        payload.companyInfo.primaryBusiness;
    }

    if (
      (action === "register" || action === "proceedToBenefitsCenter") &&
      data.companyInfo_companyName
    ) {
      localStorage.setItem("companyName", data.companyInfo_companyName);
    }

    if (action === "save-exit") {
      payload.companyInfo = payload.companyInfo || {};
      payload.companyInfo.redirectTo = "membershipdetails";
      if (!("companyDescription" in (payload.companyInfo || {}))) {
        payload.companyInfo.companyDescription = "";
      }
      if (!("programBenefits" in (payload.companyInfo || {}))) {
        payload.companyInfo.programBenefits = "";
      }
    } else if (action === "register") {
      payload.companyInfo = payload.companyInfo || {};
      payload.companyInfo.redirectTo = "membershipdetails";
    } else if (action === "proceedToBenefitsCenter") {
      payload.companyInfo = payload.companyInfo || {};
      payload.companyInfo.redirectTo = "benefitsCenter";
    }

    if (payload.membershipInfo?.membershipType) {
      payload.companyInfo = payload.companyInfo || {};
      payload.companyInfo.level = payload.membershipInfo.membershipType;
      delete payload.membershipInfo;
    }

    if (accountId) payload.id = accountId;

    const savedSelectedRegisterCompanyType = localStorage.getItem(
      "selectedRegisterCompanyType",
    );

    setIsLoading(true);
    try {
      const response: any = await saveRegistrationForm(
        PROGRAM_SPECIFIC_NAMES[PROGRAM].programType,
        payload,
      );

      if (response.status === 1) {
        if (response?.data?.errorCode === "10016") {
          displayToasts({
            type: "error",
            text: "Alternate email address is already associated with another contact",
            autoClose: true,
          });
        } else if (
          ["10004", "10008", "10001", "10003"].includes(
            response?.data?.errorCode || "",
          )
        ) {
          displayToasts({
            type: "error",
            text: "Your email is already registered.",
            autoClose: true,
          });
        } else if (response?.data?.errorCode === "10002") {
          displayToasts({
            type: "error",
            text: "You have registered with another program.",
            autoClose: true,
          });
        } else if (response?.data?.errorCode === "10009") {
          displayToasts({
            type: "error",
            text: "Company name is already registered. Please use another name",
            autoClose: true,
          });
        } else if (action === "save-exit") {
          const registerData = response?.data as {
            accId?: string;
            companyInfo?: { id?: string };
            id?: string;
          };
          const savedPartnerId =
            registerData?.accId ||
            registerData?.companyInfo?.id ||
            registerData?.id;
          if (savedPartnerId)
            localStorage.setItem("accountId", String(savedPartnerId));
          navigate(PROGRAM_SPECIFIC_NAMES[PROGRAM].route.saveSuccess);
        } else if (action === "proceedToBenefitsCenter") {
          localStorage.setItem("redirectTo", "benefitsCenter");
          localStorage.setItem("accountId", response.data.accId);
          const userLevel =
            data.membershipInfo_membershipType ||
            response.data?.companyInfo?.level;

          if (userLevel === "Silver") {
            allowNavigation();
            window.open(
              window.partnerRegistrationConfig?.REACT_SILVER_BC,
              "_self",
            );
          } else {
            const membershipTypeForSuccess =
              payload.companyInfo?.level || getStoredMembershipType();
            const companyNameForSuccess =
              data.companyInfo_companyName ||
              localStorage.getItem("companyName");
            const selectedCompanyTypeForSuccess =
              registration?.selectedRegisterCompanyType ||
              savedSelectedRegisterCompanyType;

            localStorage.clear();
            if (membershipTypeForSuccess) {
              persistPartnerLevel({ level: membershipTypeForSuccess });
            }
            if (companyNameForSuccess)
              localStorage.setItem("companyName", companyNameForSuccess);
            if (selectedCompanyTypeForSuccess)
              localStorage.setItem(
                "selectedRegisterCompanyType",
                selectedCompanyTypeForSuccess,
              );
            if (accountId) localStorage.setItem("accountId", accountId);
            allowNavigation();
            navigate(PROGRAM_SPECIFIC_NAMES[PROGRAM].route.requestSubmitted);
          }
        } else {
          const accountStatus = localStorage.getItem("accountStatus");
          const companyNameForSuccess = localStorage.getItem("companyName");
          const membershipTypeForSuccess =
            payload.companyInfo?.level || getStoredMembershipType();
          const selectedCompanyTypeForSuccess =
            registration?.selectedRegisterCompanyType ||
            savedSelectedRegisterCompanyType;

          localStorage.clear();
          if (accountStatus)
            localStorage.setItem("accountStatus", accountStatus);
          if (accountId) localStorage.setItem("accountId", accountId);
          if (companyNameForSuccess)
            localStorage.setItem("companyName", companyNameForSuccess);
          if (membershipTypeForSuccess) {
            persistPartnerLevel({ level: membershipTypeForSuccess });
          }
          if (selectedCompanyTypeForSuccess)
            localStorage.setItem(
              "selectedRegisterCompanyType",
              selectedCompanyTypeForSuccess,
            );
          allowNavigation();
          navigate(PROGRAM_SPECIFIC_NAMES[PROGRAM].route.requestSubmitted);
        }
      } else {
        displayToasts({
          type: "error",
          text: response?.message ?? "Something went wrong please try again.",
          autoClose: true,
        });
      }
    } finally {
      setIsLoading(false);
    }
  }

  const handleSaveExit = async () => {
    await submitForm("save-exit");
  };

  const handleRegisterOrCheckout = async () => {
    const action =
      membershipType === "Community" ? "register" : "proceedToBenefitsCenter";
    await submitForm(action);
  };

  const primaryButtonLabel =
    membershipType === "Community" ? "Register" : "Proceed to checkout";
  const maxVisibleBenefits = 4;

  return (
    <div className="dxp-registration-page-shell registration-page-wrapper">
      <DxpRegistrationTopHeader program={PROGRAM} />
      <div className="dxp-registration-content-row registration-content-wrapper">
        <WizardNavLayout
          previewStepOverride={previewMode ? 5 : undefined}
          previewCompanyTypeOverride={
            previewMode ? "standaloneCompany" : undefined
          }
        />
        <View UNSAFE_className="dxp-wizard-main-column">
          <View UNSAFE_className="dxp-wizard-step-panel dxp-co-step-panel-stack">
            <Flex
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              UNSAFE_className="dxp-wizard-step-heading-row dxp-wizard-flex-heading-row"
            >
              <Text UNSAFE_className="dxp-wizard-step-title-text">
                {membershipStepConfig.title}
              </Text>
              <Text UNSAFE_className="dxp-wizard-step-meta-label">
                {membershipStepConfig.stepLabel}
              </Text>
            </Flex>

            <div
              className="dxp-wizard-divider-purple dxp-wizard-divider-purple--spacious"
              aria-hidden
            />

            <Text UNSAFE_className="dxp-membership-intro-text">
              {membershipStepConfig.subtitle}
            </Text>

            <Flex
              direction="row"
              wrap
              gap="size-50"
              UNSAFE_className="dxp-membership-learn-row"
            >
              <Text UNSAFE_className="dxp-membership-body-text">
                If you are not sure what to select, learn more about our
                membership options{" "}
              </Text>
              <Link
                href="https://partners.adobe.com/digitalexperience/about"
                target="_blank"
                rel="noopener noreferrer"
              >
                here
              </Link>
              <Text UNSAFE_className="dxp-membership-body-text">.</Text>
            </Flex>

            <RadioGroup
              value={membershipType}
              onChange={setMembershipType}
              orientation="vertical"
            >
              {MEMBERSHIP_OPTIONS.map((option) => {
                const isSelected = membershipType === option.value;
                const isExpanded = expandedOptions[option.value] || false;
                const visibleBenefits = isExpanded
                  ? option.benefits
                  : option.benefits?.slice(0, maxVisibleBenefits) || [];
                const hasMoreBenefits =
                  option.benefits &&
                  option.benefits.length > maxVisibleBenefits;

                const radioCardClass = [
                  "dxp-s2-radio-card",
                  "dxp-s2-radio-card--membership",
                  "dxp-s2-radio-card--max-full",
                  isSelected
                    ? "dxp-s2-radio-card--active"
                    : "dxp-s2-radio-card--inactive",
                ].join(" ");

                return (
                  <Radio
                    key={option.value}
                    value={option.value}
                    UNSAFE_className={radioCardClass}
                  >
                    <Flex direction="column" gap="size-150" marginTop="-3px">
                      <Flex
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Text UNSAFE_className="dxp-membership-option-title">
                          {option.title}
                        </Text>
                        {option.badge ? (
                          <View UNSAFE_className="dxp-membership-badge-chip">
                            <Text UNSAFE_className="dxp-badge-text-12">
                              {option.badge}
                            </Text>
                          </View>
                        ) : null}
                      </Flex>

                      {option.value === "Silver" && option.asteriskMessage ? (
                        <div className="dxp-membership-silver-note">
                          {option.asteriskMessage}
                        </div>
                      ) : null}

                      <Flex direction="row" alignItems="start" gap={10}>
                        {option.description ? (
                          <Text UNSAFE_className="dxp-membership-benefit-desc">
                            {option.description}
                          </Text>
                        ) : null}
                        {visibleBenefits && visibleBenefits.length > 0 ? (
                          <Flex
                            direction="column"
                            gap="size-75"
                            UNSAFE_className="dxp-membership-benefits-col"
                          >
                            {visibleBenefits.map((benefit: string) => (
                              <Flex
                                key={`${option.value}-${benefit.substring(0, 20)}`}
                                direction="row"
                                alignItems="start"
                                gap="size-100"
                              >
                                <Checkmark UNSAFE_className="dxp-membership-benefit-check" />
                                <Text UNSAFE_className="dxp-membership-benefit-line">
                                  {benefit}
                                </Text>
                              </Flex>
                            ))}
                            {hasMoreBenefits ? (
                              <Flex
                                direction="row"
                                marginTop="size-100"
                                UNSAFE_className="dxp-membership-pointer-row"
                              >
                                <button
                                  type="button"
                                  className="dxp-membership-toggle-expand"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setExpandedOptions((prev) => ({
                                      ...prev,
                                      [option.value]: !prev[option.value],
                                    }));
                                  }}
                                >
                                  {isExpanded ? "View less" : "View all"}
                                </button>
                              </Flex>
                            ) : null}
                          </Flex>
                        ) : null}
                      </Flex>

                      {isSelected ? (
                        <View UNSAFE_className="dxp-membership-selected-callout">
                          <Text UNSAFE_className="dxp-membership-selected-callout-text">
                            {option.selectedMessage}
                          </Text>
                        </View>
                      ) : null}
                    </Flex>
                  </Radio>
                );
              })}
            </RadioGroup>

            <Flex
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              UNSAFE_className="dxp-wizard-footer-actions dxp-wizard-footer-actions--split dxp-wizard-footer-actions--mt-32"
            >
              <Flex
                direction="row"
                gap="size-200"
                alignItems="center"
                UNSAFE_className="dxp-wizard-footer-actions__start"
              >
                <Button
                  variant="secondary"
                  onPress={handleBack}
                  UNSAFE_className={secondaryFooterPillLabelClass}
                >
                  Back
                </Button>
                <Button
                  variant="secondary"
                  onPress={handleSaveExit}
                  UNSAFE_className={secondaryFooterPillLabelClass}
                >
                  Save and exit
                </Button>
              </Flex>
              <Button
                variant="accent"
                onPress={handleRegisterOrCheckout}
                UNSAFE_className={primaryFooterPillLabelClass}
              >
                {primaryButtonLabel}
              </Button>
            </Flex>
          </View>
        </View>
      </div>
      {isLoading && <FullScreenLoader />}
    </div>
  );
}

MembershipOptions.defaultProps = {
  previewMode: false,
};

export default MembershipOptions;
